// @flow

import React from 'react';
import styled from 'styled-components';
import { createFragmentContainer, graphql } from 'react-relay';
import { i18n } from 'shared/utils';
import SidePanel from 'shared/components/common/SidePanel';
import PanelSection from 'shared/components/common/SidePanel/PanelSection';
import MultilineText from 'shared/components/common/MultilineText';
import Divider from 'shared/components/common/Divider';
import type { CustomerSidePanel_customer as CustomerFragment } from './__generated__/CustomerSidePanel_customer';

type Props = {
  customer: CustomerFragment,
};

const AddressBlock = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AddressLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
`;

const DefaultBadge = styled.span`
  background: #3b82f6;
  color: #fff;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const renderShippingAddresses = customer => {
  const addresses = (customer.addresses && customer.addresses.slice()) || [];

  if (addresses.length === 0) {
    return <div style={{ color: '#9ca3af' }}>{i18n.t('No addresses')}</div>;
  }

  return addresses.map(addr => (
    <AddressBlock key={addr.id}>
      <AddressLabel>
        <span>{addr.label}</span>
        {addr.isDefault && <DefaultBadge>{i18n.t('DEFAULT')}</DefaultBadge>}
      </AddressLabel>
      <div>
        {addr.addressLine1}
        {addr.addressLine2 && <div>{addr.addressLine2}</div>}
        {addr.addressLine3 && <div>{addr.addressLine3}</div>}
        <div>
          {[addr.addressCity, addr.addressState, addr.addressZip]
            .filter(Boolean)
            .join(', ')}
        </div>
      </div>
    </AddressBlock>
  ));
};

const CustomerSidePanel = ({ customer }: Props) => (
  <>
    <SidePanel>
      <PanelSection
        title="Customer Info"
        fields={[
          {
            label: i18n.t('Customer #'),
            value: customer.customerNumber || '',
          },
          {
            label: i18n.t('Notes'),
            value: <MultilineText>{customer.notes}</MultilineText>,
          },
        ]}
      />
      <Divider />
      <PanelSection
        title="Secondary Contact"
        fields={[
          {
            label: i18n.t('Secondary Contact'),
            value: customer.secondaryContactName || '',
          },
          {
            label: i18n.t('Secondary Phone Number'),
            value: customer.phone2 || '',
          },
          {
            label: i18n.t('Fax'),
            value: customer.fax || '',
          },
          {
            label: i18n.t('Email'),
            value: customer.email || '',
          },
        ]}
      />
      <Divider />
      <PanelSection
        title="Address"
        fields={[
          {
            label: i18n.t('Billing Address'),
            value: (
              <div>
                {customer.billingAddressLine1}
                {customer.billingAddressLine2 && (
                  <div>{customer.billingAddressLine2}</div>
                )}
                {customer.billingAddressLine3 && (
                  <div>{customer.billingAddressLine3}</div>
                )}
                <div>
                  {customer.billingAddressCity} {customer.billingAddressState}{' '}
                  {customer.billingAddressZip}
                </div>
              </div>
            ),
          },
          {
            label: i18n.t('Shipping Addresses'),
            value: <div>{renderShippingAddresses(customer)}</div>,
          },
        ]}
      />
    </SidePanel>
  </>
);

export default createFragmentContainer(CustomerSidePanel, {
  customer: graphql`
    fragment CustomerSidePanel_customer on Customer {
      id
      customerNumber
      secondaryContactName
      addresses {
        id
        label
        addressLine1
        addressLine2
        addressLine3
        addressCity
        addressState
        addressZip
        addressCountry
        isDefault
      }
      billingAddressLine1
      billingAddressLine2
      billingAddressLine3
      billingAddressCity
      billingAddressState
      billingAddressZip
      email
      phone
      phone2
      fax
      notes
    }
  `,
});
